export * from "ce/sagas/PageSagas";
import { ModuleInstanceCreatorType } from "ee/constants/ModuleInstanceConstants";
import {
  ReduxActionErrorTypes,
  ReduxActionTypes,
} from "ee/constants/ReduxActionConstants";
import type { ReduxAction } from "actions/ReduxActionTypes";
import {
  getFeatureFlagsForEngine,
  type DependentFeatureFlags,
} from "ee/selectors/engineSelectors";
import {
  fetchPublishedPageSaga,
  saveLayoutSaga,
  createPageSaga,
  createNewPageFromEntity,
  clonePageSaga,
  updatePageSaga,
  deletePageSaga,
  savePageSaga,
  updateWidgetNameSaga,
  fetchAllPublishedPagesSaga,
  generateTemplatePageSaga,
  setPageOrderSaga,
  populatePageDSLsSaga,
  setCanvasCardsStateSaga,
  deleteCanvasCardsStateSaga,
  setPreviewModeInitSaga,
  refreshTheApp,
  fetchPageSaga,
  fetchPublishedPageResourcesSaga as fetchPublishedPageResourcesSagaCE,
  handleWidgetNameUpdateDefault,
  type HandleWidgetNameUpdatePayload,
  WidgetNameUpdateExtension,
  updateWidgetNameAPISaga,
} from "ce/sagas/PageSagas";
import {
  all,
  call,
  debounce,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
  race,
  delay,
} from "redux-saga/effects";
import { clearEvalCache } from "sagas/EvaluationsSaga";
import type {
  FetchPublishedPageResourcesPayload,
  SetupPageActionPayload,
  SetupPublishedPageActionPayload,
} from "actions/pageActions";
import {
  fetchPageAction,
  fetchPublishedPageAction,
  updateWidgetNameSuccess,
} from "actions/pageActions";
import { requestPullPackage } from "ee/actions/packageActions";
import {
  getCurrentModuleId,
  getIsCreatingUIModuleInstance,
  getModuleById,
} from "ee/selectors/modulesSelector";
import { getCurrentLayoutId } from "selectors/editorSelectors";
import type { UpdateWidgetNameRequest } from "api/PageApi";
import { setupModule, updateModuleDSL } from "ee/actions/moduleActions";
import { MAIN_MODULE_CONTAINER_WIDGET_NAME } from "ee/utils/Packages/createBaseModuleDSL";
import {
  setupModuleInstances,
  setupModuleInstancesForView,
} from "ee/actions/moduleInstanceActions";
import log from "loglevel";
import type { Module } from "ee/constants/ModuleConstants";

const UI_MODULE_INSTANCE_CREATION_TIMEOUT = 20000;

export function* setupPageSaga(action: ReduxAction<SetupPageActionPayload>) {
  try {
    const {
      id: pageId,
      isFirstLoad = false,
      packagePullStatus,
      pageWithMigratedDsl,
    } = action.payload;
    const featureFlags: DependentFeatureFlags = yield select(
      getFeatureFlagsForEngine,
    );

    if (featureFlags.showQueryModule) {
      yield put(
        requestPullPackage({
          packagePullStatus: packagePullStatus?.data,
          pageId,
        }),
      );

      yield put(
        setupModuleInstances({
          contextId: pageId,
          contextType: ModuleInstanceCreatorType.PAGE,
          viewMode: false,
        }),
      );

      yield take(ReduxActionTypes.SETUP_MODULE_INSTANCE_SUCCESS);
    }

    /*
      Added the first line for isPageSwitching redux state to be true when page is being fetched to fix scroll position issue.
      Added the second line for sync call instead of async (due to first line) as it was leading to issue with on page load actions trigger.
    */
    yield put(fetchPageAction(pageId, isFirstLoad, pageWithMigratedDsl));
    yield take(ReduxActionTypes.FETCH_PAGE_SUCCESS);

    yield put({
      type: ReduxActionTypes.SETUP_PAGE_SUCCESS,
    });
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.SETUP_PAGE_ERROR,
      payload: { error },
    });
  }
}

export function* setupPublishedPageSaga(
  action: ReduxAction<SetupPublishedPageActionPayload>,
) {
  try {
    const { bustCache, pageId, pageWithMigratedDsl } = action.payload;
    const featureFlags: DependentFeatureFlags = yield select(
      getFeatureFlagsForEngine,
    );

    if (featureFlags.showQueryModule) {
      yield put(
        setupModuleInstancesForView({
          contextId: pageId,
          contextType: ModuleInstanceCreatorType.PAGE,
          viewMode: true,
        }),
      );

      yield take(ReduxActionTypes.SETUP_MODULE_INSTANCE_FOR_VIEW_SUCCESS);
    }

    /*
      Added the first line for isPageSwitching redux state to be true when page is being fetched to fix scroll position issue.
      Added the second line for sync call instead of async (due to first line) as it was leading to issue with on page load actions trigger.
    */
    yield put(fetchPublishedPageAction(pageId, bustCache, pageWithMigratedDsl));
    yield take(ReduxActionTypes.FETCH_PUBLISHED_PAGE_SUCCESS);

    yield put({
      type: ReduxActionTypes.SETUP_PUBLISHED_PAGE_SUCCESS,
    });
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.SETUP_PUBLISHED_PAGE_ERROR,
      payload: { error },
    });
  }
}

export function* fetchPublishedPageResourcesSaga(
  action: ReduxAction<FetchPublishedPageResourcesPayload>,
) {
  const featureFlags: DependentFeatureFlags = yield select(
    getFeatureFlagsForEngine,
  );

  const { pageId } = action.payload;

  if (featureFlags.showQueryModule) {
    yield put(
      setupModuleInstancesForView({
        contextId: pageId,
        contextType: ModuleInstanceCreatorType.PAGE,
        viewMode: true,
      }),
    );

    yield take(ReduxActionTypes.SETUP_MODULE_INSTANCE_FOR_VIEW_SUCCESS);
  }

  yield call(fetchPublishedPageResourcesSagaCE, action);
}

export function* handleWidgetNameUpdate(params: HandleWidgetNameUpdatePayload) {
  const { newName, widgetName } = params;
  const moduleId: string | undefined = yield select(getCurrentModuleId);
  const layoutId: string | undefined = yield select(getCurrentLayoutId);

  if (moduleId && layoutId) {
    if (widgetName === MAIN_MODULE_CONTAINER_WIDGET_NAME) {
      throw new Error(
        "Cannot update the name of the main module container widget",
      );
    }

    const request: UpdateWidgetNameRequest = {
      newName,
      oldName: widgetName,
      moduleId,
      layoutId,
      contextType: "MODULE",
    };

    const { isValidResponse, response } = yield call(
      updateWidgetNameAPISaga,
      request,
    );

    if (isValidResponse) {
      yield put(
        updateModuleDSL({
          moduleId,
          dsl: response.data.dsl,
        }),
      );

      const module: Module = yield select(getModuleById, moduleId);

      yield put(
        setupModule({
          baseModuleId: module.baseId,
          refetchModule: true,
          resetLayout: false,
        }),
      );
      yield put(updateWidgetNameSuccess());

      return;
    }
  }

  // Not in a module context, use the default implementation
  yield call(handleWidgetNameUpdateDefault, params);
}

export function* waitForCreateUIModuleInstanceIfNeeded() {
  const isCreatingUIModuleInstance: boolean = yield select(
    getIsCreatingUIModuleInstance,
  );

  if (isCreatingUIModuleInstance) {
    try {
      // Wait for either success/error or timeout after 20 seconds
      yield race({
        moduleInstanceComplete: take([
          ReduxActionTypes.CREATE_UI_MODULE_INSTANCE_SUCCESS,
          ReduxActionErrorTypes.CREATE_UI_MODULE_INSTANCE_ERROR,
        ]),
        timeout: delay(UI_MODULE_INSTANCE_CREATION_TIMEOUT), // 20 second timeout
      });
    } catch (error) {
      // Log the error but continue with save
      log.error("Timeout waiting for UI module instance creation", error);
    }
  }
}

function* savePageWithModuleCheck(action: ReduxAction<{ isRetry?: boolean }>) {
  yield call(waitForCreateUIModuleInstanceIfNeeded);
  yield call(savePageSaga, action);
}

export default function* pageSagas() {
  WidgetNameUpdateExtension.getInstance().setExtension(handleWidgetNameUpdate);

  yield all([
    takeLatest(ReduxActionTypes.FETCH_PAGE_INIT, fetchPageSaga),
    takeLatest(
      ReduxActionTypes.FETCH_PUBLISHED_PAGE_INIT,
      fetchPublishedPageSaga,
    ),
    takeLatest(ReduxActionTypes.UPDATE_LAYOUT, saveLayoutSaga),
    takeLeading(ReduxActionTypes.CREATE_PAGE_INIT, createPageSaga),
    takeLeading(
      ReduxActionTypes.CREATE_NEW_PAGE_FROM_ENTITIES,
      createNewPageFromEntity,
    ),
    takeLeading(ReduxActionTypes.CLONE_PAGE_INIT, clonePageSaga),
    takeLatest(ReduxActionTypes.UPDATE_PAGE_INIT, updatePageSaga),
    takeLatest(ReduxActionTypes.DELETE_PAGE_INIT, deletePageSaga),
    debounce(500, ReduxActionTypes.SAVE_PAGE_INIT, savePageWithModuleCheck),
    takeLatest(ReduxActionTypes.UPDATE_WIDGET_NAME_INIT, updateWidgetNameSaga),
    takeLatest(
      ReduxActionTypes.FETCH_ALL_PUBLISHED_PAGES,
      fetchAllPublishedPagesSaga,
    ),
    takeLatest(
      ReduxActionTypes.GENERATE_TEMPLATE_PAGE_INIT,
      generateTemplatePageSaga,
    ),
    takeLatest(ReduxActionTypes.SET_PAGE_ORDER_INIT, setPageOrderSaga),
    takeLatest(ReduxActionTypes.POPULATE_PAGEDSLS_INIT, populatePageDSLsSaga),
    takeEvery(ReduxActionTypes.SET_CANVAS_CARDS_STATE, setCanvasCardsStateSaga),
    takeEvery(
      ReduxActionTypes.DELETE_CANVAS_CARDS_STATE,
      deleteCanvasCardsStateSaga,
    ),
    takeEvery(ReduxActionTypes.SET_PREVIEW_MODE_INIT, setPreviewModeInitSaga),
    takeLatest(ReduxActionTypes.REFRESH_THE_APP, refreshTheApp),
    takeLatest(ReduxActionTypes.CLEAR_CACHE, clearEvalCache),
    takeLatest(ReduxActionTypes.SETUP_PAGE_INIT, setupPageSaga),
    takeLatest(
      ReduxActionTypes.SETUP_PUBLISHED_PAGE_INIT,
      setupPublishedPageSaga,
    ),
    takeLatest(
      ReduxActionTypes.FETCH_PUBLISHED_PAGE_RESOURCES_INIT,
      fetchPublishedPageResourcesSaga,
    ),
  ]);
}
